<script>
import Card from '../../../Components/Card.vue';
import Swal from 'sweetalert2';
import SlideOut from '../../../Components/SlideOut.vue';
import { closeModal, openModal } from '../../../Modules/Modals.js';
import SimpleSelect from '../../../Components/Inputs/SimpleSelect.vue';
import Label from '../../../Components/Inputs/Label.vue';
import { useForm } from '@inertiajs/vue3';
import TextInput from '../../../Components/Inputs/TextInput.vue';
import { NUMBER } from '../../../Constants/InputTypes.js';
import Button from '../../../Components/Button.vue';
import Templates from '../../Marketing/Templates.vue';
import { confirm } from '@/Modules/Confirmation.js';
import Alert from '@/Components/Alert.vue';
import Loading from '@/Components/Loading.vue';
import { genericError } from '@/Modules/Alerts.js';

export default {
  components: { Loading, Alert, Templates, Button, TextInput, Label, SimpleSelect, SlideOut, Card },

  props: {
    allowAutoAdvance: {
      type: Boolean,
      default: true,
    },
    masterConfig: Object,
    kioskKey: String,
    customer: Number,
    msoUserIds: {
      type: Array,
      default: () => [],
    },
    newCustomer: Object,
    newVehicle: Object,
    vehicleID: Number,
    selectedVehicleId: Number,
    persistedVehicles: {
      type: Array,
      default: () => [],
    },
  },

  emits: ['update', 'ready', 'next'],

  data() {
    return {
      vehiclePlaceholder: new URL('/resources/img/vehicle_placeholder.svg', import.meta.url).href,
      sadPlaceholder: new URL('/resources/img/sad.svg', import.meta.url).href,
      addVehicleModal: 'addVehicleModal',
      vehiclesLoading: false,
      form: useForm({
        year: null,
        make: null,
        model: null,
        mileage: null,
        license: null,
        license_state: null,
        newVehicle: true,
      }),
      states: {},
      years: {},
      models: {},
      makes: {},

      selectedVehicle: null,

      vehicles: [],
      vehicleSearch: null,
      page: 1,
      total: 0,
      perPage: 12,
      loadingMore: false,
      searchTimer: null,

      makesLoading: false,
      modelsLoading: false,
      autoSelectedVehicle: false,
    };
  },

  computed: {
    isNewVehicleReady() {
      if (this.form.make == null) {
        return false;
      }

      if (this.form.make == null) {
        return false;
      }

      if (this.form.model == null) {
        return false;
      }

      if (this.masterConfig.license_required) {
        if (!this.form.license) {
          return false;
        }

        if (!this.form.license_state) {
          return false;
        }
      }

      return true;
    },

    licenseFieldsMissing() {
      if (!this.masterConfig.license_required) return null;
      const missingLicense = !this.form.license;
      const missingState = !this.form.license_state;
      if (!missingLicense && !missingState) return null;
      if (missingLicense && missingState)
        return `${this.licenseSpelling(true)} and state are required to add a vehicle.`;
      if (missingLicense) return `${this.licenseSpelling(true)} is required to add a vehicle.`;
      return `${this.licenseSpelling(true)} state is required to add a vehicle.`;
    },

    // Search and paging are now handled server-side; this stays a passthrough
    // so the template keeps iterating a single list (page results + client-added
    // vehicles merged on page 1).
    filteredVehicles() {
      return this.vehicles;
    },

    hasMore() {
      return this.vehicles.length < this.total;
    },

    hasAddedVehicle() {
      const isAddedVehicle = (vehicle) =>
        vehicle?.newVehicle === true && vehicle.vehicle_id === undefined;

      return this.persistedVehicles.some(isAddedVehicle) || this.vehicles.some(isAddedVehicle);
    },
  },

  watch: {
    vehicleSearch() {
      // Only re-query the server when we have a customer/MSO context to search.
      if (this.customer === null && this.msoUserIds.length === 0) {
        return;
      }
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.page = 1;
        this.getVehicles({ append: false });
      }, 300);
    },

    'form.year'(newValue, oldValue) {
      if (oldValue !== null) {
        this.form.make = null;
        this.form.model = null;
      }
      this.getMakes();
    },

    'form.make'(newValue, oldValue) {
      if (oldValue !== null) {
        this.form.model = null;
      }
      this.getModels();
    },
  },

  mounted() {
    if (this.customer !== null || this.msoUserIds.length > 0) {
      this.getVehicles();
    } else {
      this.vehicles = this.mergePersistedVehicles([]);
      const hasRestoredVehicle = this.restoreSelectedVehicle();

      if (!hasRestoredVehicle) {
        this.openModal();
      }

      if (this.newVehicle && this.newVehicle.make !== null) {
        Object.keys(this.newVehicle).forEach((key) => {
          this.form[key] = this.newVehicle[key];
        });

        if (this.form.year) this.getMakes();
        if (this.form.make) this.getModels();

        if (this.masterConfig.license_required) {
          setTimeout(() => {
            const licenseInput = document.querySelector(
              `#${this.addVehicleModal} input[placeholder*="License"]`,
            );
            if (licenseInput) {
              licenseInput.focus();
            }
          }, 300);
        }
      }
    }
    this.getStates();
    this.getYears();
  },

  methods: {
    NUMBER() {
      return NUMBER;
    },

    openModal() {
      if (this.hasAddedVehicle) {
        return;
      }

      openModal(this.addVehicleModal);
    },

    getYears() {
      axios
        .post(
          route('scheduler.vehicles.years', {
            key: this.kioskKey,
          }),
        )
        .then((response) => {
          let years = response.data.data.years;
          const sortedEntries = Object.entries(years)
            .sort((a, b) => Number(b[0]) - Number(a[0]))
            .map(([key, value]) => [String(key), String(value)]);
          this.years = Object.fromEntries(sortedEntries);
        });
    },

    getMakes() {
      if (this.form.year == null || this.form.year.length === 0) {
        return;
      }
      this.makesLoading = true;

      axios
        .post(
          route('scheduler.vehicles.makes', {
            key: this.kioskKey,
          }),
          {
            year: this.form.year,
          },
        )
        .then((response) => {
          this.makes = response.data.data.makes;
        })
        .catch((error) => {
          this.makes = {};
        })
        .finally(() => {
          this.makesLoading = false;
        });
    },

    selectVehicle(vehicle) {
      if (this.isSelectedVehicle(vehicle)) {
        this.selectedVehicle = null;
        return;
      }

      if (vehicle.latest_open_status !== null && vehicle.latest_open_status !== undefined) {
        Swal.fire({
          text: 'Vehicle has an open appointment or is currently in service. You cannot select this vehicle.',
          icon: 'error',
        });
      } else {
        this.confirmedSelectedVehicle(vehicle);
      }
    },

    confirmedSelectedVehicle(vehicle, autoSelected = false) {
      this.selectedVehicle = vehicle;
      this.$emit('ready', true);

      const source = vehicle?.is_external_shop
        ? {
            shop_id: vehicle.shop_id,
            user_id: vehicle.user_id,
            vehicle_id: vehicle.vehicle_id,
          }
        : null;

      this.$emit('update', {
        vehicle: vehicle?.is_external_shop ? null : vehicle.vehicle_id,
        msoSource: source,
        newVehicle: vehicle,
      });

      if (autoSelected && this.allowAutoAdvance) {
        this.autoSelectedVehicle = true;
        // Small delay to ensure the state is updated before advancing
        setTimeout(() => {
          this.$emit('next');
        }, 500);
      }
    },

    autoSelectVehicle() {
      if (this.vehicleID && this.vehicles.length > 0) {
        const vehicle = this.vehicles.find((v) => v.vehicle_id == this.vehicleID);
        if (vehicle) {
          this.confirmedSelectedVehicle(vehicle, true);
        }
      }
    },

    sameVehicle(vehicle, compareVehicle) {
      if (!vehicle || !compareVehicle) {
        return false;
      }

      if (vehicle.vehicle_id !== undefined && compareVehicle.vehicle_id !== undefined) {
        return vehicle.vehicle_id === compareVehicle.vehicle_id;
      }

      return (
        String(vehicle.year ?? '')
          .trim()
          .toLowerCase() ===
          String(compareVehicle.year ?? '')
            .trim()
            .toLowerCase() &&
        String(vehicle.make ?? '')
          .trim()
          .toLowerCase() ===
          String(compareVehicle.make ?? '')
            .trim()
            .toLowerCase() &&
        String(vehicle.model ?? '')
          .trim()
          .toLowerCase() ===
          String(compareVehicle.model ?? '')
            .trim()
            .toLowerCase() &&
        String(vehicle.license ?? '')
          .trim()
          .toLowerCase() ===
          String(compareVehicle.license ?? '')
            .trim()
            .toLowerCase() &&
        String(vehicle.license_state ?? '')
          .trim()
          .toLowerCase() ===
          String(compareVehicle.license_state ?? '')
            .trim()
            .toLowerCase()
      );
    },

    isSelectedVehicle(vehicle) {
      return vehicle === this.selectedVehicle;
    },

    mergePersistedVehicles(vehicles) {
      const mergedVehicles = [...vehicles];

      this.persistedVehicles.forEach((persistedVehicle) => {
        const existingIndex = mergedVehicles.findIndex((vehicle) =>
          this.sameVehicle(vehicle, persistedVehicle),
        );

        if (existingIndex === -1) {
          mergedVehicles.unshift({
            ...persistedVehicle,
            newVehicle: true,
          });
        } else if (persistedVehicle.vehicle_id === undefined) {
          mergedVehicles[existingIndex] = {
            ...mergedVehicles[existingIndex],
            ...persistedVehicle,
            newVehicle: true,
          };
        }
      });

      return mergedVehicles;
    },

    restoreSelectedVehicle() {
      if (this.selectedVehicleId && this.vehicles.length > 0) {
        const vehicle = this.vehicles.find((item) => item.vehicle_id == this.selectedVehicleId);
        if (vehicle) {
          this.selectedVehicle = vehicle;
          this.$emit('ready', true);
          return true;
        }
      }

      if (
        this.newVehicle &&
        this.newVehicle.year &&
        this.newVehicle.make &&
        this.newVehicle.model
      ) {
        const vehicle = this.vehicles.find((item) => this.sameVehicle(item, this.newVehicle));
        if (vehicle) {
          this.selectedVehicle = vehicle;
          this.$emit('ready', true);
          return true;
        }
      }

      return false;
    },

    matchTireConnectVehicle() {
      if (
        this.selectedVehicle !== null ||
        !this.newVehicle ||
        !this.newVehicle.year ||
        !this.newVehicle.make ||
        !this.newVehicle.model
      ) {
        return;
      }

      const tcYear = String(this.newVehicle.year).toLowerCase().trim();
      const tcMake = String(this.newVehicle.make).toLowerCase().trim();
      const tcModel = String(this.newVehicle.model).toLowerCase().trim();
      const tcName = `${tcYear} ${tcMake} ${tcModel}`;

      let matchedVehicle = null;

      for (const vehicle of this.vehicles) {
        const vYear = String(vehicle.year || '')
          .toLowerCase()
          .trim();
        const vMake = String(vehicle.make || '')
          .toLowerCase()
          .trim();
        const vModel = String(vehicle.model || '')
          .toLowerCase()
          .trim();
        const vName = `${vYear} ${vMake} ${vModel}`;

        if (vName === tcName && !vehicle.latest_open_status_exists) {
          matchedVehicle = vehicle;
          break;
        }
      }

      if (matchedVehicle) {
        this.confirmedSelectedVehicle(matchedVehicle, true);
      } else {
        Object.keys(this.newVehicle).forEach((key) => {
          this.form[key] = this.newVehicle[key];
        });

        if (this.form.year) this.getMakes();
        if (this.form.make) this.getModels();

        if (this.form.year && this.form.make && this.form.model) {
          this.openModal();

          if (this.masterConfig.license_required) {
            setTimeout(() => {
              const licenseInput = document.querySelector(
                `#${this.addVehicleModal} input[placeholder*="License"]`,
              );
              if (licenseInput) {
                licenseInput.focus();
              }
            }, 300);
          }
        }
      }
    },

    getModels() {
      if (this.form.make == null || this.form.make.length === 0) {
        return;
      }
      this.modelsLoading = true;

      axios
        .post(
          route('scheduler.vehicles.models', {
            key: this.kioskKey,
          }),
          {
            year: this.form.year,
            make: this.form.make,
          },
        )
        .then((response) => {
          this.models = response.data.data.models;
        })
        .catch((error) => {
          this.models = {};
        })
        .finally(() => {
          this.modelsLoading = false;
        });
    },

    addVehicle() {
      if (this.hasAddedVehicle) {
        return;
      }

      const newVehicle = {
        ...this.form.data(),
        newVehicle: true,
      };

      this.$emit('update', {
        newVehicle,
      });
      this.vehicles = this.mergePersistedVehicles([newVehicle, ...this.vehicles]);
      this.selectVehicle(newVehicle);
      this.form.year = null;
      this.form.make = null;
      this.form.model = null;
      this.form.mileage = null;
      this.form.license = null;
      this.form.license_state = null;
      this.form.clearErrors();
      this.closeModal();
      this.$emit('ready', true);
    },

    getVehicles({ append = false } = {}) {
      if (append) {
        this.loadingMore = true;
      } else {
        this.vehiclesLoading = true;
      }

      axios
        .post(
          route('scheduler.vehicles.list', {
            key: this.kioskKey,
          }),
          {
            customer_id: this.customer,
            mso_user_ids: this.msoUserIds,
            page: this.page,
            search: this.vehicleSearch,
          },
        )
        .then((response) => {
          const fetched = response.data.data.vehicles;
          this.total = response.data.data.total ?? fetched.length;

          if (append) {
            this.vehicles = [...this.vehicles, ...fetched];
          } else {
            this.vehicles = this.mergePersistedVehicles(fetched);
            if (!this.restoreSelectedVehicle()) {
              this.autoSelectVehicle();
              this.matchTireConnectVehicle();
            }
          }
        })
        .catch(genericError)
        .finally(() => {
          this.vehiclesLoading = false;
          this.loadingMore = false;
        });
    },

    loadMore() {
      this.page++;
      this.getVehicles({ append: true });
    },

    closeModal() {
      this.form.year = null;
      this.form.make = null;
      this.form.model = null;
      this.form.mileage = null;
      this.form.license = null;
      this.form.license_state = null;
      this.form.clearErrors();
      this.years = {};
      this.models = {};
      this.makes = {};
      closeModal(this.addVehicleModal);
      this.getYears();
    },

    updateForm(field, value) {
      this.form[field] = value;
    },

    getStates() {
      axios
        .post(
          route('scheduler.states', {
            key: this.kioskKey,
          }),
        )
        .then((response) => {
          this.states = response.data.data.states;
        });
    },

    licenseSpelling(include_plate_wording = false) {
      var license_spelling;
      if (this.masterConfig && this.masterConfig.country) {
        license_spelling =
          this.masterConfig.country == 'AU'
            ? 'Rego'
            : include_plate_wording
              ? 'License Plate'
              : 'License';
      } else {
        license_spelling = include_plate_wording ? 'License Plate' : 'License';
      }
      return license_spelling;
    },
  },
};
</script>

<template>
  <Card class="mt-3">
    <div class="form-group mb-4">
      <Label>Search for Vehicle</Label>
      <TextInput
        :model-value="vehicleSearch"
        @keyup:modelValue="(newValue) => (vehicleSearch = newValue)"
        placeholder="Search for vehicle"
      />
    </div>

    <Loading v-if="vehiclesLoading" />

    <div class="row">
      <template v-for="vehicle in filteredVehicles">
        <div class="col-md-4 mb-4">
          <div
            class="card vehicle-card h-100 text-center"
            :class="{
              selected: isSelectedVehicle(vehicle),
              'vehicle-card-available': !vehicle.latest_open_status_exists,
              'vehicle-card--locked': vehicle.latest_open_status_exists,
            }"
          >
            <div
              class="vehicle-card-img-wrapper"
              :class="{ 'no-click': vehicle.latest_open_status_exists }"
              v-on:click="selectVehicle(vehicle)"
            >
              <template v-if="vehicle?.default_picture?.thumbnail_url !== undefined">
                <img
                  :src="vehicle.default_picture.thumbnail_url"
                  class="vehicle-card-img vehicle-card-img--photo"
                />
              </template>
              <template v-else>
                <img
                  :src="vehiclePlaceholder"
                  class="vehicle-card-img vehicle-card-img--placeholder"
                />
              </template>
            </div>

            <template v-if="vehicle.latest_open_status_exists">
              <div class="vehicle-status-alert">
                <Alert class="warning">
                  {{ vehicle.latest_open_status.message.message_short }}
                </Alert>
              </div>
            </template>

            <div
              class="card-body"
              :class="{ 'no-click': vehicle.latest_open_status_exists }"
              v-on:click="selectVehicle(vehicle)"
            >
              <h6 class="card-title mb-1">
                {{ vehicle.full_vehicle !== undefined ? vehicle.full_vehicle : $vehicleLabel(vehicle) }}
              </h6>
              <p v-if="vehicle.source_shop_name" class="text-muted small mb-1">
                Shop: {{ vehicle.source_shop_name }}
              </p>
              <template
                v-if="
                  vehicle.latest_completed_status !== undefined &&
                  vehicle.latest_completed_status !== null
                "
              >
                <p class="text-muted small">
                  {{
                    (vehicle?.latest_completed_status?.mileage ??
                    vehicle.latest_open_status?.kilometer)
                      ? Number(
                          vehicle?.latest_completed_status?.mileage ??
                            vehicle.latest_open_status?.kilometer,
                        ).toLocaleString() +
                        ' ' +
                        masterConfig.mileage_spelling
                      : 'Mileage not available'
                  }}
                </p>
              </template>
              <template v-else>
                <p
                  v-if="vehicle.mileage !== undefined && vehicle.mileage !== null"
                  class="text-muted small"
                >
                  {{ vehicle.mileage }} {{ masterConfig.mileage_spelling }}
                </p>
                <p v-else class="text-muted small">
                  {{ masterConfig.mileage_spelling }} not available
                </p>
              </template>

              <button
                :disabled="vehicle.latest_open_status_exists === true"
                v-on:click="selectVehicle(vehicle)"
                class="btn btn-secondary btn-sm"
              >
                Select Vehicle
              </button>
            </div>
          </div>
        </div>
      </template>
    </div>

    <div class="text-center my-3" v-if="hasMore && !vehiclesLoading">
      <button class="btn btn-outline-secondary" :disabled="loadingMore" @click="loadMore">
        {{ loadingMore ? 'Loading…' : 'Load more' }}
      </button>
    </div>

    <div class="text-center my-2" v-if="!vehiclesLoading && !hasAddedVehicle">
      <!-- SVG Placeholder -->
      <div class="mb-3">
        <img :src="sadPlaceholder" alt="Vehicle not found" style="width: 200px" />
      </div>

      <!-- Add Vehicle Button -->
      <button class="btn btn-outline-primary" @click="openModal">
        Add Vehicle <span class="ml-1">+</span>
      </button>
    </div>
  </Card>

  <SlideOut :id="addVehicleModal" title="Add Vehicle">
    <div class="form-group mb-3">
      <Label :required="true">Year</Label>
      <SimpleSelect
        order="desc"
        :options="years"
        :model-value="form.year"
        @update:model-value="(newValue) => updateForm('year', newValue)"
      />
    </div>

    <div class="form-group mb-3">
      <Label :required="true">Make</Label>
      <SimpleSelect
        :disabled="form.year == null || makesLoading == true"
        :options="makes"
        :model-value="form.make"
        @update:model-value="(newValue) => updateForm('make', newValue)"
      />
    </div>

    <div class="form-group mb-3">
      <Label :required="true">Model</Label>
      <SimpleSelect
        :disabled="form.make == null || modelsLoading == true"
        :options="models"
        :model-value="form.model"
        @update:model-value="(newValue) => updateForm('model', newValue)"
      />
    </div>

    <div class="form-group mb-3">
      <Label :required="false">Mileage</Label> <i>(optional)</i>
      <TextInput
        :type="NUMBER()"
        :model-value="form.mileage"
        placeholder="Enter Mileage"
        @blur:model-value="(newValue) => updateForm('mileage', newValue)"
      />
    </div>

    <div class="form-group mb-3">
      <Label :required="this.masterConfig.license_required">{{ licenseSpelling() }}</Label>
      <i v-if="!this.masterConfig.license_required">(optional)</i>
      <TextInput
        :model-value="form.license"
        :placeholder="'Enter ' + licenseSpelling(true)"
        @blur:model-value="(newValue) => updateForm('license', newValue)"
      />
    </div>

    <div class="form-group mb-3">
      <Label :required="this.masterConfig.license_required"
        >{{ licenseSpelling(true) }} State</Label
      >
      <i v-if="!this.masterConfig.license_required">(optional)</i>
      <SimpleSelect
        empty-placeholder="Select State"
        :options="states"
        :model-value="form.license_state"
        @update:model-value="(newValue) => updateForm('license_state', newValue)"
      />
    </div>

    <div v-if="licenseFieldsMissing" class="license-required-banner mb-3">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
        stroke-linejoin="round"
        style="vertical-align: -1px; margin-right: 5px; flex-shrink: 0"
      >
        <path
          d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
        />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
      {{ licenseFieldsMissing }}
    </div>

    <template #footer>
      <Button class-name="btn btn-secondary" @click="closeModal()"> Close </Button>

      <Button :disabled="isNewVehicleReady === false" @click="addVehicle()"> Add Vehicle </Button>
    </template>
  </SlideOut>
</template>

<style scoped>
.card.vehicle-card.selected {
  border: 2px solid #343a40;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.4);
  transition:
    box-shadow 0.3s,
    border-color 0.3s;
}

.vehicle-status-alert {
  position: absolute;
  width: 100%;
  top: 70px;
}

.card.vehicle-card-available:hover {
  box-shadow: 0 0 10px 4px rgba(0, 123, 255, 0.5);
  border-color: #007bff;
}

.vehicle-card--locked {
  opacity: 0.65;
  cursor: not-allowed !important;
}

.vehicle-card--locked .no-click {
  pointer-events: none;
}

.vehicle-card--locked .btn {
  cursor: not-allowed;
}

.vehicle-card-img-wrapper {
  width: 100%;
  height: 160px;
  overflow: hidden;
  cursor: pointer;
  background-color: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: calc(0.375rem - 1px) calc(0.375rem - 1px) 0 0;
}

.vehicle-card-img {
  width: 100%;
  height: 100%;
  display: block;
}

.vehicle-card-img--photo {
  object-fit: cover;
  object-position: center;
}

.vehicle-card-img--placeholder {
  object-fit: contain;
  padding: 20px;
  opacity: 0.4;
}

.license-required-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  color: #842029;
  background-color: #f8d7da;
  border: 1px solid #f5c2c7;
  border-radius: 4px;
  padding: 8px 12px;
  text-align: center;
}
</style>
